import { Transition } from '@headlessui/react'
import {
  PencilSquareIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { Fragment, useRef, useState } from 'react'

export type ListSide = 'PRO' | 'CON'

export interface ListItemData {
  id: string
  label: string
  weight: number
  side: ListSide
  optionId?: string
}

interface ListItemProps extends ListItemData {
  isTouchDevice?: boolean
  onEdit: (id: string, label: string) => void
  onDelete: (id: string) => void
  onToggleWeight: (id: string, currentWeight: number) => void
  editLabel?: string
  deleteLabel?: string
  markKeyPointLabel?: string
  unmarkKeyPointLabel?: string
}

const SWIPE_ACTION_WIDTH = 80

export function ListItem({
  id,
  label,
  weight,
  side,
  isTouchDevice = false,
  onEdit,
  onDelete,
  onToggleWeight,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  markKeyPointLabel = 'Mark as key point',
  unmarkKeyPointLabel = 'Unmark as key point',
}: ListItemProps) {
  const [showButtons, setShowButtons] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(label)
  const [swipeOffset, setSwipeOffsetState] = useState(0)

  const editDoneRef = useRef(false)
  const swipeOffsetRef = useRef(0)
  const touchStartRef = useRef<{
    x: number
    y: number
    baseOffset: number
  } | null>(null)
  const isTouchingRef = useRef(false)
  const isHorizontalRef = useRef(false)

  function updateSwipeOffset(value: number) {
    swipeOffsetRef.current = value
    setSwipeOffsetState(value)
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      baseOffset: swipeOffsetRef.current,
    }
    isTouchingRef.current = true
    isHorizontalRef.current = false
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!touchStartRef.current) return
    const dx = e.touches[0].clientX - touchStartRef.current.x
    const dy = e.touches[0].clientY - touchStartRef.current.y
    if (!isHorizontalRef.current) {
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return
      if (Math.abs(dy) >= Math.abs(dx)) {
        touchStartRef.current = null
        isTouchingRef.current = false
        return
      }
      isHorizontalRef.current = true
    }
    updateSwipeOffset(
      Math.max(
        -SWIPE_ACTION_WIDTH,
        Math.min(0, touchStartRef.current.baseOffset + dx),
      ),
    )
  }

  function handleTouchEnd() {
    isTouchingRef.current = false
    if (!isHorizontalRef.current) {
      if (swipeOffsetRef.current !== 0) updateSwipeOffset(0)
      touchStartRef.current = null
      return
    }
    updateSwipeOffset(
      swipeOffsetRef.current < -(SWIPE_ACTION_WIDTH / 2)
        ? -SWIPE_ACTION_WIDTH
        : 0,
    )
    touchStartRef.current = null
    isHorizontalRef.current = false
  }

  function confirmEdit() {
    if (editDoneRef.current) return
    editDoneRef.current = true
    if (editValue === '') {
      onDelete(id)
    } else {
      onEdit(id, editValue)
    }
    setEditing(false)
  }

  function cancelEdit() {
    editDoneRef.current = true
    setEditValue(label)
    setEditing(false)
  }

  function startEditing() {
    updateSwipeOffset(0)
    editDoneRef.current = false
    setEditing(true)
  }

  const isWeighted = weight > 0

  return (
    <Transition
      as={Fragment}
      appear
      show
      enter="transition-opacity duration-150"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <li
        data-test={`${side.toLowerCase()}-reason`}
        className="relative overflow-hidden border-b border-[rgba(128,128,128,0.07)] last:border-b-0"
      >
        {editing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              confirmEdit()
            }}
            className="px-3.5 py-2.5 w-full"
          >
            <input
              type="text"
              autoFocus
              data-test="edit-reason-input"
              className="border-0 border-b border-[rgba(128,128,128,0.4)] border-dashed pb-px outline-none transition-[border-color,border-style] duration-150 focus:border-solid focus:border-ink dark:focus:border-dark-ink w-full bg-transparent text-base md:text-sm font-medium text-ink dark:text-dark-ink"
              onKeyDown={(e) => {
                if (e.key === 'Escape') cancelEdit()
              }}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={confirmEdit}
              value={editValue}
            />
          </form>
        ) : (
          <>
            {isTouchDevice && (
              <div
                className="absolute inset-y-0 right-0 flex"
                style={{ width: SWIPE_ACTION_WIDTH }}
              >
                <button
                  data-test="swipe-edit-reason-button"
                  onClick={startEditing}
                  aria-label={editLabel}
                  className="flex-1 flex items-center justify-center bg-blue-500 text-white"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
                <button
                  data-test="swipe-delete-reason-button"
                  onClick={() => onDelete(id)}
                  aria-label={deleteLabel}
                  className="flex-1 flex items-center justify-center text-white"
                  style={{ backgroundColor: 'var(--zine-con)' }}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )}
            <div
              data-test="reason-swipe-content"
              className="flex items-center gap-2.5 px-3.5 py-2.5 text-md font-medium leading-normal"
              style={{
                backgroundColor: 'var(--zine-card-bg)',
                ...(isTouchDevice && {
                  transform: `translateX(${swipeOffset}px)`,
                  transition: isTouchingRef.current
                    ? 'none'
                    : 'transform 0.2s ease',
                }),
              }}
              onMouseEnter={() => setShowButtons(true)}
              onMouseLeave={() => setShowButtons(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <button
                data-test={
                  isWeighted
                    ? 'decrease-reason-weight-button'
                    : 'increase-reason-weight-button'
                }
                onClick={() => onToggleWeight(id, weight)}
                aria-label={
                  isWeighted ? unmarkKeyPointLabel : markKeyPointLabel
                }
                className="bg-transparent border-0 cursor-pointer p-0 shrink-0 flex items-center transition-[color,transform] duration-150 hover:scale-[1.2] hover:!text-ink dark:hover:!text-dark-ink"
              >
                {isWeighted ? (
                  <StarSolidIcon
                    className={`${isTouchDevice ? 'h-5 w-5' : 'h-4 w-4'} text-gray-600 dark:text-white`}
                  />
                ) : (
                  <StarIcon
                    className={`${isTouchDevice ? 'h-5 w-5' : 'h-4 w-4'} text-gray-400 dark:text-gray-500`}
                  />
                )}
              </button>
              <span
                className={`flex-1 min-w-0 text-ink dark:text-dark-ink transition-colors cursor-default ${isWeighted ? 'font-bold' : ''} ${showButtons && !isTouchDevice ? 'truncate' : ''}`}
                onDoubleClick={startEditing}
              >
                {label}
              </span>
              {!isTouchDevice && showButtons && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-test="edit-reason-button"
                    onClick={() => setEditing(true)}
                    aria-label={editLabel}
                    className="text-ink-secondary dark:text-dark-ink2 opacity-40 hover:opacity-100 transition-opacity p-0 bg-transparent border-0 cursor-pointer"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-test="delete-reason-button"
                    onClick={() => onDelete(id)}
                    aria-label={deleteLabel}
                    className="bg-transparent border-0 cursor-pointer text-sm text-ink-secondary dark:text-dark-ink2 opacity-40 hover:opacity-100 transition-opacity duration-[120ms] px-0.5 shrink-0"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </li>
    </Transition>
  )
}
